import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  DataTable,
  DataTableCellContent,
  DataTableColumnSettings,
  DataTableEmpty,
  DataTableEntitySelectorCell,
  DataTableFilterButton,
  DataTableGroupByDropdown,
  DataTableCellTrigger,
  DataTablePopoverCell,
  DataTableToolbar,
  DataTableToolbarLeft,
  DataTableToolbarActions,
  useDataTableState,
  createColumnHelper,
  renderDefaultGroupActions,
  resolveVisibleGroups,
  UNASSIGNED_GROUP_KEY,
  Box,
  Text,
  type DataTableColumnDef,
  type BadgeMapEntry,
  type EntityItemConfig,
  type EntitySelectorConfig,
  type BulkAction,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
} from "@salestrak/ui";

import { RoleSelector } from "#app/components/entity-selectors";
import { teamToPoolItem } from "#app/components/entity-selectors/hooks/use-team-pool";
import { renderTableEntityFilter } from "#app/components/entity-selectors/table-entity-filter";
import { EntityTag, EntityGroupTag, type EntityData } from "#app/components/entity";
import { statusBadgeVariant } from "#app/components/entity/badge-tone";
import { useTableFilters, type FilterDef, type UseTableFiltersResult } from "#app/lib/hooks/use-table-filters";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import { MemberAccessCountButton } from "#app/components/members/member-access-popover";
import { MemberRowMenuItems, MemberRowMenuDialog, type ConfirmAction } from "#app/components/members/member-row-menu";
import { ExpandableSearch } from "@salestrak/ui";
import { combineWhereConditions, buildOrderBy } from "#app/lib/query-utils";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import {
  type MemberRow,
  MembersGroupDataDocument,
  MembersListDataDocument,
  type MembersListDataQuery,
  type RoleRow,
  type TeamOption,
} from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type MembersTableVariant = "app" | "settings";

// Shared by the table (sticky pinning) and the column settings panel (locked
// rows). The panel ignores structural ids like "select".
const MEMBERS_PINNED_COLUMN_IDS = ["select", "name"];

type MembersTableProps = Omittable<{
  where: Record<string, unknown>;
  orderBy: Record<string, string> | undefined;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  searchValue: string;
  onSearchChange: (value: string) => void;
  variant?: MembersTableVariant;
  // App variant
  onViewProfile?: (userId: string) => void;
  // Settings variant
  onNavigateToProfile?: (userId: string) => void;
  availableTeams?: TeamOption[];
  availableRoles?: RoleRow[];
  onTeamsChange?: (userId: string, currentTeamIds: string[], newTeamIds: string[]) => void;
  onRoleChange?: (userId: string, currentRoleIds: string[], roleId: string) => void;
  onFilterWhereChange?: (where: Record<string, unknown> | undefined) => void;
  actionButton?: ReactNode;
  // Per-group queries
  searchWhere?: Record<string, unknown>;
  /** Called when sibling data (roles, teams) is extracted from the ungrouped query. */
  onSiblingData?: (roles: RoleRow[], teams: TeamOption[]) => void;
  /** When provided, the table uses this filter state instead of creating its own. Pair with `hideToolbarActions` when filter/search UI lives in a parent header. */
  filterState?: UseTableFiltersResult<EntityItemConfig>;
  hideToolbarActions?: boolean;
  // Bulk actions (settings variant)
  bulkActions?: BulkAction<MemberRow>[];
}>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// `statusBadgeVariant` is the shared tone → Badge variant mapper.
const MEMBER_STATUS_BADGE: Record<string, BadgeMapEntry> = {
  active: { label: "Active", variant: statusBadgeVariant("success") },
  suspended: { label: "Suspended", variant: statusBadgeVariant("muted") },
  invited: { label: "Invited", variant: statusBadgeVariant("info") },
};

const h = createColumnHelper<MemberRow>();

// ---------------------------------------------------------------------------
// Per-group query helpers
// ---------------------------------------------------------------------------

const getConnection = (data: any) => data?.users;

// ---------------------------------------------------------------------------
// Multi-select teams cell (settings variant)
// ---------------------------------------------------------------------------

function MemberTeamsCell({
  userId,
  currentTeams,
  availableTeams,
  onCommit,
}: {
  userId: string;
  currentTeams: { teamId: string; teamName: string; teamKey?: string | null; icon?: { icon?: string | null; color?: string | null } | null }[];
  availableTeams: TeamOption[];
  onCommit: (userId: string, teamIds: string[]) => void;
}) {
  const teamPool = useMemo(
    () => ({
      id: "teams",
      label: "Teams",
      items: availableTeams.map(teamToPoolItem),
    }),
    [availableTeams],
  );

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [teamPool],
      searchPlaceholder: "Search teams...",
      emptyMessage: "No teams found",
    }),
    [teamPool],
  );

  const teamIds = useMemo(() => currentTeams.map((t) => t.teamId), [currentTeams]);

  return (
    <DataTableEntitySelectorCell
      config={config}
      value={teamIds}
      onCommit={(ids) => onCommit(userId, ids)}
      contentProps={{ align: "start" }}
    >
      {currentTeams.length === 0 ? (
        <Text size="sm" variant="muted">None</Text>
      ) : (
        <EntityGroupTag
          entities={currentTeams.map(
            (team): EntityData => ({ kind: "team", id: team.teamId, team }),
          )}
          variant="expanded"
          size="sm"
          title="Teams"
          interactive={false}
        />
      )}
    </DataTableEntitySelectorCell>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

type UseMembersColumnsParams = Omittable<{
  variant: MembersTableVariant;
  // App variant
  onViewProfile?: (userId: string) => void;
  // Settings variant
  onNavigateToProfile?: (userId: string) => void;
  onTeamsChange?: (userId: string, currentTeamIds: string[], newTeamIds: string[]) => void;
  onRoleChange?: (userId: string, currentRoleIds: string[], roleId: string) => void;
  availableTeams: TeamOption[];
}>;

// The role cell renders the member's OWN role; the pickable set is the role
// editor's, not a column's.
function useMembersColumns({
  variant,
  onViewProfile,
  onNavigateToProfile,
  onTeamsChange,
  onRoleChange,
  availableTeams,
}: UseMembersColumnsParams): DataTableColumnDef<MemberRow>[] {
  return useMemo<DataTableColumnDef<MemberRow>[]>(
    () => {
      const handler = variant === "app" ? onViewProfile : onNavigateToProfile;

      const nameColumn = h.hoverAction({
        accessorKey: "name",
        header: "Name",
        enableSorting: true,
        meta: variant === "settings" ? { minWidth: 160, maxWidth: 280 } : undefined,
        content: (m) => (
          <EntityTag
            entity={{
              kind: "user",
              id: m.userId,
              user: {
                name: m.name,
                email: m.email,
                username: variant === "app" ? m.username : undefined,
                profileImage: m.profileImage,
                memberStatus: m.memberStatus,
              },
            }}
            subtitle={variant === "app"}
            interactive={false}
          />
        ),
        onClickAction: (m) => handler?.(m.userId),
        actionLabel: (m) => `View profile for ${m.name ?? m.email}`,
      });

      const statusColumn = h.badge({
        accessorKey: "memberStatus",
        header: "Status",
        enableSorting: true,
        badgeMap: MEMBER_STATUS_BADGE,
        fallback: "active",
      });

      const teamsColumn = h.display({
        id: "teams",
        header: "Teams",
        meta: { minWidth: 140 },
        cell: ({ row }) => {
          const teams = row.original.teams;

          if (variant === "app") {
            return (
              <DataTableCellContent align="start">
                {teams.length === 0 ? (
                  <Text size="sm" variant="muted">None</Text>
                ) : teams.length === 1 ? (
                  // Single team: labelled tag instead of a redundant one-avatar stack.
                  <EntityTag
                    entity={{ kind: "team", id: at(teams, 0, "team").teamId, team: at(teams, 0, "team") }}
                    padded
                  />
                ) : (
                  <EntityGroupTag
                    entities={teams.map(
                      (team): EntityData => ({
                        kind: "team",
                        id: team.teamId,
                        team,
                      }),
                    )}
                    title="Teams"
                  />
                )}
              </DataTableCellContent>
            );
          }

          return (
            <MemberTeamsCell
              userId={row.original.userId}
              currentTeams={teams}
              availableTeams={availableTeams}
              onCommit={(userId, newTeamIds) => onTeamsChange?.(userId, teams.map((t) => t.teamId), newTeamIds)}
            />
          );
        },
      });

      const rolesColumn = h.display({
        id: "roles",
        header: "Role",
        cell: ({ row }) => {
          const roles = row.original.roles;
          const roleText =
            roles.length === 0
              ? "None"
              : roles.map((r) => r.roleName).join(", ");

          const roleContent = (
            <Text size="sm" variant="muted" truncate={1}>
              {roleText}
            </Text>
          );

          if (variant === "app") {
            return (
              <DataTableCellContent>{roleContent}</DataTableCellContent>
            );
          }

          return (
            <DataTablePopoverCell
              contentProps={{ className: "p-1" }}
              content={(close) => (
                <RoleSelector
                  variant="inline"
                  value={roles[0]?.roleId ?? null}
                  onValueChange={(roleId) => { onRoleChange?.(row.original.userId, roles.map((r) => r.roleId), roleId); close(); }}
                />
              )}
            >
              {roleContent}
            </DataTablePopoverCell>
          );
        },
      });

      const emailColumn = h.accessor({
        accessorKey: "email",
        header: "Email",
        enableSorting: true,
        meta: { minWidth: 300 },
        cell: ({ getValue }) => (
          <DataTableCellContent>
            <Text size="sm" variant="muted" truncate={1}>{getValue() as string}</Text>
          </DataTableCellContent>
        ),
      });

      const joinedColumn = h.date({ accessorKey: "creationDate", header: "Joined" });

      // Access column (settings only) — share-only role tiers
      // (hasPublicAccess=false) get the "N Workflows  N Items" cell;
      // each count is its own button opening the per-user popover on
      // that tab. Public-access roles see everything public already,
      // so the cell stays blank for them.
      const accessColumn = h.display({
        id: "access",
        header: "Access",
        meta: { minWidth: 160 },
        cell: ({ row }) => {
          const m = row.original;
          if (m.hasPublicAccess) {
            return <DataTableCellContent>{null}</DataTableCellContent>;
          }
          return (
            <DataTableCellTrigger>
              <DataTableCellContent>
                <Box direction="row" align="center" gap="sm">
                  <MemberAccessCountButton
                    userId={m.userId}
                    tab="workflows"
                    label={`${m.sharedWorkflowCount} ${
                      m.sharedWorkflowCount === 1 ? "Workflow" : "Workflows"
                    }`}
                  />
                  <MemberAccessCountButton
                    userId={m.userId}
                    tab="items"
                    label={`${m.sharedItemCount} ${
                      m.sharedItemCount === 1 ? "Item" : "Items"
                    }`}
                  />
                </Box>
              </DataTableCellContent>
            </DataTableCellTrigger>
          );
        },
      });

      const cols: DataTableColumnDef<MemberRow>[] = [nameColumn];
      if (variant === "settings") cols.push(emailColumn, statusColumn);
      cols.push(teamsColumn, rolesColumn);
      if (variant === "settings") cols.push(accessColumn);
      cols.push(joinedColumn);
      return cols;
    },
    [
      variant,
      onViewProfile,
      onNavigateToProfile,
      onTeamsChange,
      onRoleChange,
      availableTeams,
    ]
  );
}

// ---------------------------------------------------------------------------
// Filter options
// ---------------------------------------------------------------------------

const MEMBER_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "invited", label: "Invited" },
  { value: "suspended", label: "Suspended" },
];

export function buildMembersFilterDefs(
  teams: TeamOption[],
  roles: RoleRow[],
  variant: MembersTableVariant = "settings",
): FilterDef<EntityItemConfig>[] {
  const teamPoolItems = teams.map(teamToPoolItem);
  const defs: FilterDef<EntityItemConfig>[] = [
    {
      key: "teamIds",
      label: "Team",
      icon: "users",
      type: "entity",
      pools: teams.length > 0 ? [{ id: "teams", label: "Teams", items: teamPoolItems }] : [],
      placeholder: "Search teams...",
      filterFormat: "idArray",
    },
    {
      key: "roleIds",
      label: "Role",
      icon: "shield",
      type: "select",
      options: roles.map((r) => ({ value: r.roleId, label: r.roleName })),
    },
  ];
  if (variant === "settings") {
    defs.push({
      key: "memberStatus",
      label: "Status",
      icon: "circle",
      type: "select",
      options: MEMBER_STATUS_OPTIONS,
      operator: "eq",
      multiple: false,
    });
  }
  return defs;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MembersTable({
  where: whereProp,
  orderBy: orderByProp,
  sorting,
  onSortingChange,
  searchValue,
  onSearchChange,
  variant = "app",
  onViewProfile,
  onNavigateToProfile,
  availableTeams = [],
  availableRoles = [],
  onTeamsChange,
  onRoleChange,
  onFilterWhereChange,
  searchWhere,
  onSiblingData,
  bulkActions,
  actionButton,
  filterState: externalFilterState,
  hideToolbarActions,
}: MembersTableProps) {
  // --- Member row menu dialog state (lifted outside MenuContent) ---
  const [memberConfirmAction, setMemberConfirmAction] = useState<ConfirmAction | null>(null);

  const filterDefs = useMemo(
    () => buildMembersFilterDefs(availableTeams, availableRoles, variant),
    [availableTeams, availableRoles, variant],
  );
  const internalFilterState = useTableFilters(externalFilterState ? [] : filterDefs);
  const tableFilters = externalFilterState ?? internalFilterState;

  useEffect(() => {
    onFilterWhereChange?.(tableFilters.where);
  }, [tableFilters.where, onFilterWhereChange]);

  const rawColumns = useMembersColumns({
    variant,
    onViewProfile,
    onNavigateToProfile,
    onTeamsChange,
    onRoleChange,
    availableTeams,
  });

  // Per-user column customization, keyed by variant — the app and settings
  // members tables show different column sets.
  const tableView = useTableViewSettings(`members:${variant}`);

  const {
    columns,
    groupByField,
    setGroupByField,
    isGrouped,
    groupByLabels,
    groupByIcons,
    activeGroupConfig,
    groupSortDirection,
    setGroupSortDirection,
  } = useDataTableState({
    columns: rawColumns,
    groupFields: {
      teams: {
        label: "Team",
        icon: "users",
        groups: [
          ...availableTeams.map((t) => ({ key: t.teamId, label: t.teamName })),
          { key: UNASSIGNED_GROUP_KEY, label: "No team" },
        ],
        filterKey: "teamIds",
      },
      roles: {
        label: "Role",
        icon: "shield",
        groups: [
          ...availableRoles.map((r) => ({ key: r.roleId, label: r.roleName })),
          { key: UNASSIGNED_GROUP_KEY, label: "No role" },
        ],
        filterKey: "roleIds",
      },
    },
  });

  const groupWhereBuilders = useMemo<Record<string, (key: string) => Record<string, unknown>>>(() => ({
    teams: (key) =>
      key === UNASSIGNED_GROUP_KEY
        ? { teamIds: { isEmpty: true } }
        : { teamIds: { hasSome: [key] } },
    roles: (key) =>
      key === UNASSIGNED_GROUP_KEY
        ? { roleIds: { isEmpty: true } }
        : { roleIds: { hasSome: [key] } },
  }), []);

  // --- Group query state (used for both grouped and ungrouped) ---
  const { states, registerState, getGroupProps } = useGroupStates<MemberRow>();

  // Extract sibling data (roles, teams) from the ungrouped query result.
  const onSiblingDataRef = useRef(onSiblingData);
  onSiblingDataRef.current = onSiblingData;
  const handleUngroupedData = useCallback((rawQueryData: MembersListDataQuery | null) => {
    // Sibling connections ride along on the ungrouped query — the nodes ARE
    // the option types, so nothing is re-shaped on the way out.
    const roles = rawQueryData?.roles?.edges.map((e) => e.node) ?? [];
    const teams = rawQueryData?.teams?.edges.map((e) => e.node) ?? [];
    if (roles.length > 0 || teams.length > 0) {
      onSiblingDataRef.current?.(roles, teams);
    }
  }, []);

  // Determine visible groups when grouped.
  const visibleGroups = useMemo(() => {
    if (!isGrouped || !activeGroupConfig) return [];
    return resolveVisibleGroups(activeGroupConfig, tableFilters.filters, groupSortDirection);
  }, [isGrouped, activeGroupConfig, tableFilters.filters, groupSortDirection]);

  // Build the base where for per-group queries: search + non-group filters.
  const orderBy = useMemo(
    () => (orderByProp !== undefined ? orderByProp : sorting.length === 0 ? undefined : buildOrderBy(sorting, "name")),
    [orderByProp, sorting],
  );

  const groupBaseWhere = useMemo(() => {
    if (!isGrouped || !activeGroupConfig?.filterKey) {
      // No group filter to strip — combine search with all filters.
      const conditions: Record<string, unknown>[] = [];
      if (searchWhere) conditions.push(searchWhere);
      if (tableFilters.where) conditions.push(tableFilters.where);
      return combineWhereConditions(conditions);
    }
    // Strip the group-related filter from the filter where.
    const strippedFilterWhere = tableFilters.getWhereExcluding(activeGroupConfig.filterKey);
    const conditions: Record<string, unknown>[] = [];
    if (searchWhere) conditions.push(searchWhere);
    if (strippedFilterWhere) conditions.push(strippedFilterWhere);
    return combineWhereConditions(conditions);
  }, [isGrouped, activeGroupConfig, searchWhere, tableFilters]);

  // Build DataTable groups.
  const groups = useMemo<DataTableGroupType<MemberRow>[]>(() => {
    if (!isGrouped) {
      return [{
        key: "__all__",
        label: "All Members",
        totalCount: states.get("__all__")?.total,
        ...getGroupProps("__all__"),
      }];
    }
    // Build groups from per-group query states.
    return visibleGroups.map((g) => ({
      key: g.key,
      label: g.label,
      totalCount: states.get(g.key)?.total,
      ...getGroupProps(g.key),
    }));
  }, [isGrouped, visibleGroups, states, getGroupProps]);

  const renderGroupTitle = useCallback(
    (groupKey: string) => {
      if (groupKey === UNASSIGNED_GROUP_KEY) {
        return (
          <Text size="sm" variant="muted">
            {groupByField === "teams" ? "No team" : "No role"}
          </Text>
        );
      }
      if (groupByField === "teams") {
        const team = availableTeams.find((t) => t.teamId === groupKey);
        return (
          <EntityTag
            entity={{ kind: "team", id: groupKey, team: team ?? { teamName: groupKey } }}
            padded
          />
        );
      }
      const group = visibleGroups.find((g) => g.key === groupKey);
      return group?.label ?? groupKey;
    },
    [groupByField, availableTeams, visibleGroups]
  );

  return (
    <>
      {/* Ungrouped query provider */}
      {!isGrouped && (
        <GroupQueryProvider<MemberRow>
          groupKey="__all__"
          document={MembersListDataDocument}
          variables={{ where: whereProp, orderBy, limit: 50 }}
          getConnection={getConnection}
          onStateChange={registerState}
          onData={handleUngroupedData}
        />
      )}

      {/* Per-group query providers (renderless) */}
      {isGrouped && groupWhereBuilders[groupByField] && visibleGroups.map((g) => {
        const groupWhere = combineWhereConditions(
          [groupBaseWhere, groupWhereBuilders[groupByField]?.(g.key)].filter(Boolean) as Record<string, unknown>[],
        );
        return (
          <GroupQueryProvider<MemberRow>
            key={g.key}
            groupKey={g.key}
            document={MembersGroupDataDocument}
            variables={{ where: groupWhere, orderBy, limit: 50 }}
            getConnection={getConnection}
              onStateChange={registerState}
          />
        );
      })}

      <DataTableToolbar>
        <DataTableToolbarLeft>
          <DataTableGroupByDropdown
            groupByField={groupByField}
            setGroupByField={setGroupByField}
            groupByLabels={groupByLabels}
            groupByIcons={groupByIcons}
            groupSortDirection={groupSortDirection}
            setGroupSortDirection={setGroupSortDirection}
          />
        </DataTableToolbarLeft>
        {!hideToolbarActions && (
          <DataTableToolbarActions>
            <DataTableFilterButton
              filters={tableFilters.filters}
              activeFilterCount={tableFilters.activeFilterCount}
              clearAll={tableFilters.clearAll}
              setFilterValues={tableFilters.setFilterValues}
              renderEntityFilter={renderTableEntityFilter}
            />
            <ExpandableSearch
              onValueChange={onSearchChange}
              placeholder="Search members..."
              size="xs"
            />
            <DataTableColumnSettings
              columns={columns}
              pinnedColumnIds={MEMBERS_PINNED_COLUMN_IDS}
              {...tableView.settingsProps}
            />
            {actionButton}
          </DataTableToolbarActions>
        )}
      </DataTableToolbar>

      <Box direction="column" fill="flex">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(r) => r.userId}
          sorting={sorting}
          onSortingChange={onSortingChange}
          stickyHeader
          density="regular"
          pinnedColumnIds={MEMBERS_PINNED_COLUMN_IDS}
          fillColumnId="name"
          paddingX="lg"
          {...tableView.tableProps}
          groupTitle={isGrouped ? renderGroupTitle : undefined}
          groupActions={renderDefaultGroupActions}
          bulkActions={variant === "settings" ? bulkActions : undefined}
          renderRowActions={variant === "settings" ? (member) => (
            <MemberRowMenuItems
              member={member}
              availableRoles={availableRoles}
              onViewProfile={(userId) => onNavigateToProfile?.(userId)}
              onRoleChange={(userId, roleId) => onRoleChange?.(userId, member.roles.map((r) => r.roleId), roleId)}
              onRequestSuspend={(m) => setMemberConfirmAction({ type: "suspend", member: m })}
              onRequestReactivate={(m) => setMemberConfirmAction({ type: "reactivate", member: m })}
              onRequestCancelInvite={(m) => setMemberConfirmAction({ type: "cancel-invite", member: m })}
            />
          ) : undefined}
          emptyState={
            <DataTableEmpty
              message={
                searchValue
                  ? "No members match your search"
                  : "No members found"
              }
            />
          }
        />
      </Box>

      <MemberRowMenuDialog
        action={memberConfirmAction}
        onClose={() => setMemberConfirmAction(null)}
      />
    </>
  );
}
